import Analytics from '@opencare/analytics';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { Authenticate } from 'connect/saga/handlers/user/authenticate';
import { selectDataUserAuthenticated } from 'connect/selectors';
import { StepFormWrapper, StepHeader, StepHeaderSubtext, StepHeaderText, StepWrapper } from 'pages/checkout/components/common/layout-styled-components';
import { CheckoutStateHandler } from 'pages/checkout/connect/state-handlers';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';

import { AppointmentDetails } from './components/AppointmentDetails/AppointmentDetails';
import { AuthenticationForm } from './components/AuthenticationForm/AuthenticationForm';
import { OtpForm } from './components/AuthenticationForm/OtpForm';
import { LoggedIn } from './components/LoggedIn/LoggedIn';

const analytics = new Analytics();

enum FormState {
  AUTHENTICATION,
  OTP_VERIFICATION,
  LOGGED_IN,
}

export const Account = () => {
  const [formState, setFormState] = useState<FormState>(FormState.AUTHENTICATION);
  const analyticsData = useCheckoutRedesignAnalyticsData();

  // AuthenticationForm will call this saga handler, so we can watch it here to determine if we need to show the OTP form
  const authenticateResponse = useSelector(Authenticate.value!.selector);

  const otpSent = useMemo(
    () => authenticateResponse?.otpSent || false,
    [authenticateResponse],
  );

  const isUserAuthenticated = useSelector(selectDataUserAuthenticated);
  const checkoutState = useSelector(CheckoutStateHandler.selector);
  const { email } = checkoutState?.account || {};

  useEffect(() => {
    analytics.trackPageView({
      pageType: analytics.pageTypes.CHECKOUT,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'account-info',
      ...analyticsData,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- analyticsData is a new object every time, so we don't want it in the dependency array
  }, []);

  useEffect(() => {
    // Push setting state action to end of event loop so the forms can handle auth success
    const timeout = setTimeout(() => {
      const newFormState = (() => {
        if (isUserAuthenticated) {
          return FormState.LOGGED_IN;
        }

        if (otpSent && email) {
          return FormState.OTP_VERIFICATION;
        }

        return FormState.AUTHENTICATION;
      })();

      setFormState(newFormState);
    });

    return () => clearTimeout(timeout);
  }, [isUserAuthenticated, otpSent, email]);

  const authForm = useMemo(
    () => ({
      [FormState.AUTHENTICATION]: <AuthenticationForm />,
      [FormState.OTP_VERIFICATION]: <OtpForm />,
      [FormState.LOGGED_IN]: <LoggedIn />,
    }[formState]),
    [formState],
  );

  return (
    <StepWrapper>
      <StepFormWrapper>
        <StepHeader>
          <StepHeaderText>Book your appointment</StepHeaderText>
          <StepHeaderSubtext>We will keep your information safe and secure.</StepHeaderSubtext>
        </StepHeader>

        <AppointmentDetails />

        {authForm}
      </StepFormWrapper>
    </StepWrapper>
  );
};
